import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import {
  listLabels,
  batchAnalyzeLabels,
  promoteLabelToProject,
  clearAiTags,
  type Label,
} from "@/lib/labels-api";
import {
  getGetTodayQueryKey,
  getListProjectsQueryKey,
  getListTasksQueryKey,
} from "@workspace/api-client-react";
import LabelBadge from "@/components/label-badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Loader2, MoreHorizontal, Sparkles, Tag, FolderPlus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export const LABELS_QUERY_KEY = ["labels"] as const;

interface Props {
  /** Currently active label filter (a label id or null = no filter). */
  selectedLabelId: string | null;
  onSelectLabel: (labelId: string | null) => void;
}

const PROMOTE_THRESHOLD = 5;

export default function LabelsFilterBar({
  selectedLabelId,
  onSelectLabel,
}: Props) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [pendingPromote, setPendingPromote] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: LABELS_QUERY_KEY,
    queryFn: () => listLabels(),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const labels = data?.labels ?? [];
  const sorted = useMemo(
    () =>
      [...labels].sort((a, b) => {
        if (b.taskCount !== a.taskCount) return b.taskCount - a.taskCount;
        return a.name.localeCompare(b.name);
      }),
    [labels],
  );

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: LABELS_QUERY_KEY });
    qc.invalidateQueries({ queryKey: getGetTodayQueryKey() });
    qc.invalidateQueries({ queryKey: getListTasksQueryKey() });
    qc.invalidateQueries({ queryKey: getListProjectsQueryKey() });
    qc.invalidateQueries({ queryKey: ["task-labels"] });
  };

  const analyzeMutation = useMutation({
    mutationFn: () => batchAnalyzeLabels(),
    onSuccess: (r) => {
      invalidateAll();
      if (r.reason === "ai_cap_reached") {
        toast({
          title: "Quota IA quotidien atteint",
          description: "Réessayez demain ou augmentez votre quota.",
          variant: "destructive",
        });
      } else if (r.reason === "ai_failed") {
        toast({
          title: "L'analyse IA a échoué",
          description: "Réessayez dans quelques instants.",
          variant: "destructive",
        });
      } else if (r.reason === "already_running") {
        toast({
          title: "Analyse en cours",
          description:
            "Une analyse précédente est encore en train de tourner — patientez quelques secondes.",
        });
      } else if (r.tasksWithLabels === 0) {
        toast({
          title: "Aucune nouvelle étiquette pertinente",
          description:
            r.analysed === 0
              ? "Toutes vos tâches récentes sont déjà étiquetées."
              : "L'IA n'a pas trouvé de regroupement clair pour le moment.",
        });
      } else {
        toast({
          title: `${r.tasksWithLabels} tâche${r.tasksWithLabels > 1 ? "s" : ""} étiquetée${r.tasksWithLabels > 1 ? "s" : ""}`,
          description:
            r.newLabelsCreated > 0
              ? `${r.newLabelsCreated} nouvelle${r.newLabelsCreated > 1 ? "s" : ""} étiquette${r.newLabelsCreated > 1 ? "s" : ""} créée${r.newLabelsCreated > 1 ? "s" : ""}.`
              : undefined,
        });
      }
    },
    onError: (err: Error) => {
      toast({
        title: "Échec de l'analyse",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const promoteMutation = useMutation({
    mutationFn: (labelId: string) => promoteLabelToProject(labelId),
    onMutate: (labelId) => {
      setPendingPromote(labelId);
    },
    onSettled: () => setPendingPromote(null),
    onSuccess: (r) => {
      invalidateAll();
      toast({
        title: `Projet « ${r.project.name} » créé`,
        description:
          r.attachedTaskCount > 0
            ? `${r.attachedTaskCount} tâche${r.attachedTaskCount > 1 ? "s" : ""} rattachée${r.attachedTaskCount > 1 ? "s" : ""}.`
            : "Aucune tâche n'a été déplacée (toutes étaient déjà dans un projet).",
        action: (
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(`/app/projects/${r.project.id}`)}
          >
            Ouvrir
          </Button>
        ),
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Échec de la création de projet",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const clearMutation = useMutation({
    mutationFn: () => clearAiTags(),
    onSuccess: () => {
      invalidateAll();
      toast({ title: "Étiquettes IA effacées" });
    },
    onError: (err: Error) =>
      toast({
        title: "Échec",
        description: err.message,
        variant: "destructive",
      }),
  });

  // The bar is intentionally minimal: hide entirely when nothing exists yet
  // and the user hasn't started an analysis. The AI button still surfaces
  // through the toolbar above if the user wants to seed the system.
  if (isLoading) return null;

  const hasAny = sorted.length > 0;

  return (
    <div
      className="flex items-center gap-2 flex-wrap mb-4"
      data-testid="labels-filter-bar"
    >
      <div className="inline-flex items-center gap-1 text-xs text-muted-foreground mr-1">
        <Tag className="w-3.5 h-3.5" />
        <span>Étiquettes</span>
      </div>

      {hasAny && (
        <button
          type="button"
          onClick={() => onSelectLabel(null)}
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
            selectedLabelId === null
              ? "bg-foreground text-background border-foreground"
              : "border-border/60 text-muted-foreground hover:text-foreground hover:border-border"
          }`}
          data-testid="label-filter-all"
        >
          Toutes
        </button>
      )}

      {sorted.map((label) => {
        const promoteEligible =
          label.orphanTaskCount >= PROMOTE_THRESHOLD &&
          label.taskCount >= PROMOTE_THRESHOLD;
        return (
          <div key={label.id} className="inline-flex items-center gap-0.5">
            <LabelBadge
              name={`${label.name} · ${label.taskCount}`}
              color={label.color}
              size="sm"
              active={selectedLabelId === label.id}
              onClick={() =>
                onSelectLabel(
                  selectedLabelId === label.id ? null : label.id,
                )
              }
            />
            {promoteEligible && (
              <button
                type="button"
                onClick={() => promoteMutation.mutate(label.id)}
                disabled={pendingPromote === label.id}
                title={`Créer un projet à partir de l'étiquette ${label.name} (${label.orphanTaskCount} tâches non rattachées)`}
                className="ml-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full border border-dashed border-border/60 text-muted-foreground hover:text-foreground hover:border-border"
                data-testid={`label-promote-${label.id}`}
              >
                {pendingPromote === label.id ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <FolderPlus className="w-3 h-3" />
                )}
              </button>
            )}
          </div>
        );
      })}

      <div className="ml-auto flex items-center gap-1">
        {hasAny && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0"
                aria-label="Actions étiquettes"
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-xs">
                Maintenance
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => clearMutation.mutate()}
                disabled={clearMutation.isPending}
                className="text-destructive focus:text-destructive"
              >
                Retirer toutes les étiquettes IA
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
